<template>
  <CoreDialog
    data-qa="doctor-avatar-editor-preview"
    :value="value"
    :size="isMobile ? 'fullscreen' : 'xl'"
    :title="title"
    :min-height="isMobile ? undefined : '650px'"
    divider
    persistent
  >
    <template #prependBtn="{ btnProps }">
      <v-btn
        v-bind="btnProps"
        @click="emit('cancel')"
      >
        <span class="ui-icon-close-not-a-circle" />
      </v-btn>
    </template>

    <div
      v-if="isLoading"
      class="d-flex flex-column justify-center align-center flex-grow-1"
    >
      <v-progress-circular
        color="primary"
        size="48"
        indeterminate
      />
      <div class="text-body-2 text--text mt-4">
        {{ t('Обрабатываем искусственным интеллектом', l10nOptions) }}
      </div>
    </div>
    <div
      v-else
      class="doctor-avatar-editor-modal d-flex flex-column align-center flex-shrink-0 flex-grow-1"
    >
      <div
        v-if="!isStaff"
        class="doctor-avatar-editor-modal__info mb-4 w-100 pa-4 text-body-2 text--text"
        :class="isMobile ? 'mx-2' : ''"
      >
        {{ t('Фото обработано. Если с фотографией что-то не так, загрузите другую.', l10nOptions) }}
      </div>
      <v-img
        class="flex-grow-0"
        height="300"
        width="300"
        :src="imageSrc"
        :class="isMobile ? 'mb-4' : 'mb-2'"
        alt=""
      />
      <div class="d-flex mb-6">
        <v-btn
          class="pa-0 mr-2"
          color="white"
          width="36"
          min-width="36"
          height="36"
          elevation="1"
          @click="emit('rotate-clockwise')"
        >
          <v-icon color="primary">
            ui-icon-undo
          </v-icon>
        </v-btn>
        <v-btn
          class="pa-0"
          color="white"
          width="36"
          min-width="36"
          height="36"
          elevation="1"
          @click="emit('rotate-counter-clockwise')"
        >
          <v-icon color="primary">
            ui-icon-redo
          </v-icon>
        </v-btn>
      </div>
      <div>
        <v-switch
          v-if="isStaff"
          v-model="isCropPhoto"
          class="mb-6 mt-0 pt-0"
          hide-details
          inset
          @change="emit('switch-crop-photo', isCropPhoto)"
        >
          <template #label>
            <span class="text-body-1 text--text">
              {{ t('Обтравить фото', l10nOptions) }}
            </span>
          </template>
        </v-switch>
        <v-switch
          v-if="isUpscalingEnabled"
          v-model="isUpscalePhoto"
          class="mb-6 mt-0 pt-0"
          hide-details
          inset
          @change="emit('switch-upscale-photo', isUpscalePhoto)"
        >
          <template #label>
            <span class="text-body-1 text--text">
              {{ t('Включить улучшение', l10nOptions) }}<br>
              {{ t('изображения', l10nOptions) }}
            </span>
          </template>
        </v-switch>
      </div>
    </div>

    <template
      v-if="!isLoading"
      #actions
    >
      <v-btn
        color="text"
        :class="{ 'flex-grow-1 w-0': isMobile }"
        text
        @click="emit('cancel')"
      >
        {{ t('Отменить', l10nOptions) }}
      </v-btn>
      <v-btn
        color="primary"
        :class="{ 'flex-grow-1 w-0': isMobile }"
        depressed
        @click="emit('submit')"
      >
        {{ t('Отправить', l10nOptions) }}
      </v-btn>
    </template>
  </CoreDialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import i18next from 'i18next'
import useIsMobile from 'src/composables/useIsMobile'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'

/**
 * Диалог превью аватара: поворот, обтравка и отправка на модерацию.
 *
 * Переключатели сбрасываются при каждом открытии, чтобы не тащить
 * прошлое состояние на новый файл.
 */
const { t, language } = i18next
const l10nOptions = { ns: language }
const isMobile = useIsMobile()

const props = withDefaults(defineProps<{
  value: boolean
  title: string
  isLoading: boolean
  imageSrc: string
  isStaff: boolean
  isUpscalingEnabled: boolean
  isInitUpscalePhoto: boolean
}>(), {
  imageSrc: '',
  isStaff: false,
  isUpscalingEnabled: false,
})

type Emits = {
  (_event: 'cancel'): void
  (_event: 'submit'): void
  (_event: 'rotate-clockwise'): void
  (_event: 'rotate-counter-clockwise'): void
  (_event: 'switch-crop-photo', _value: boolean): void
  (_event: 'switch-upscale-photo', _value: boolean): void
}

const emit = defineEmits<Emits>()
const isCropPhoto = ref(true)
const isUpscalePhoto = ref(props.isInitUpscalePhoto)

watch(() => props.value, () => {
  isCropPhoto.value = true
  isUpscalePhoto.value = props.isInitUpscalePhoto
  emit('switch-crop-photo', true)
  emit('switch-upscale-photo', props.isInitUpscalePhoto)
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';
@import 'src/styles/mixins';

.doctor-avatar-editor-modal {
  &__info {
    background-color: $ui-kit-bg-secondary;

    @include banner-info-background;
  }
}
</style>
